import * as React from "react";
import { withTranslation, WithTranslation, useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { FormatData } from "utils/FormatData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EmailUploadFile } from "../Stores/EmailSubmitStore";
import { DownOutlined } from "@ant-design/icons";
import { Row, Button, Dropdown, Tooltip } from "antd";
import { EmailExternalFileUpload } from "../EmailSendModal/EmailFileUpload";
import { EmailAttachmentsTableActions } from "./EmailAttachmentsTableActions";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { EmailAttachmentsTableItemActions } from "./EmailAttachmentsTableItemActions";
import { projectsDataStore } from "Stores";
import { ItemType } from "antd/lib/menu/interface";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";

const FilenameCell = ({ file }: { file: EmailUploadFile }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  if (file.status === "uploading") {
    return (
      <span>
        {file.name} <FontAwesomeIcon icon={["far", "spinner"]} spin={true} style={{ marginLeft: 5 }} />
      </span>
    );
  } else if (file.status === "error") {
    return (
      <Tooltip title={t("Failed to upload file. Please, remove file from the list and upload it again.")}>
        <span className="color-red">
          {file.name} <FontAwesomeIcon icon={["far", "times-circle"]} style={{ marginLeft: 5 }} />
        </span>
      </Tooltip>
    );
  }
  return <span>{file.name}</span>;
};

interface EmailAttachmentsTableProps extends WithTranslation {
  data: EmailUploadFile[];
  toggleFilesModal: () => void;
  loading: boolean;
  removeFile: (uid: string) => void;
}

interface EmailAttachmentsTableState {
  selectedFiles: EmailUploadFile[];
  selectedFileKeys: string[];
}

export const EmailAttachmentsTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<EmailAttachmentsTableProps, EmailAttachmentsTableState> {
      constructor(props: EmailAttachmentsTableProps) {
        super(props);
        this.state = {
          selectedFiles: [],
          selectedFileKeys: [],
        };
      }

      resetSelection = () => this.setState({ selectedFiles: [] });

      removeSelectedFiles = () => {
        const { selectedFileKeys } = this.state;
        selectedFileKeys.forEach((uid) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!isNaN(uid as any)) {
            uid = `@id${uid}`;
          }
          this.props.removeFile(uid);
        });
        this.setState({ selectedFileKeys: [], selectedFiles: [] });
      };

      removeSingleFile = (fileId) => {
        const fileIndex = this.state.selectedFileKeys.findIndex((item) => item === fileId);
        if (fileIndex !== -1) {
          const { selectedFileKeys, selectedFiles } = this.state;
          selectedFileKeys.splice(fileIndex, 1);
          selectedFiles.splice(fileIndex, 1);
          this.setState({ selectedFileKeys: [], selectedFiles: [] });
        }
        this.props.removeFile(fileId);
      };

      handleSelectRows = ({ selectedRows, selectedRowKeys }) => {
        this.setState({ selectedFileKeys: selectedRowKeys, selectedFiles: selectedRows });
      };

      renderFileType = ({ rowData: file }: { rowData: EmailUploadFile | API_FileDataEntity }) => {
        // consider that if file was added from project - it would always have "parentId" prop
        if (!(file as API_FileDataEntity).parentId) {
          return file.type;
        } else {
          // consider that extension is in the last part after dot
          const splittedFileName = file.name.split(".");
          if (splittedFileName.length > 1) {
            // if filename has extension - length would be > 1
            return splittedFileName[splittedFileName.length - 1];
          } else {
            return "";
          }
        }
      };

      downloadSelectedFiles = () => {
        if (!this.state.selectedFiles.length) {
          return;
        }
        documentsTableStore.downloadFiles(this.state.selectedFiles.map((file) => file.fileDataEntity));
      };

      onSingleActionSelect = (key: string, data: EmailUploadFile) => {
        switch (key) {
          case "download": {
            const file = data.fileDataEntity;
            documentsTableStore.downloadFiles([file]);
            break;
          }
          case "apryse_view":
            apryseViewerStore.fetch(+data.id, { fileName: data.name, canDownload: data.fileActions.download });
            break;
          case "easy_view":
            pdfViewerStore.fetch(+data.id, { fileName: data.name, canDownload: data.fileActions.download });
            break;
          case "wopi_view":
            wopiViewerStore.fetch(+data.id, { fileName: data.name, edit: data.viewers.office365Edit });
            break;
          case "online_view":
            onlineViewerStore.fetch(+data.id, { fileName: data.name });
            break;
          case "remove":
            this.removeSingleFile(data.uid);
            break;
          default:
            break;
        }
      };

      get selectedFilesCanNotBeDownloaded() {
        return this.state.selectedFiles.some((file) => !file.fileActions || !file.fileActions.download);
      }

      render() {
        const { t, data, toggleFilesModal } = this.props;

        const menuItems: ItemType[] = [
          {
            key: "internal",
            onClick: toggleFilesModal,
            label: t("Project files"),
          },
        ];
        if (
          projectsDataStore.currentProject.infoMailSettings &&
          !projectsDataStore.currentProject.infoMailSettings.mailDontAllowAppendExternalFiles
        ) {
          menuItems.push({
            key: "external",
            style: { padding: 0 },
            label: (
              <EmailExternalFileUpload>
                <div style={{ padding: "4px 12px" }}>{t("Upload files")}</div>
              </EmailExternalFileUpload>
            ),
          });
        }
        const columns: TableColumnProps<EmailUploadFile>[] = [
          // { dataKey: 'uid', label: t('uid'), width: 100 },
          {
            dataKey: "name",
            label: t("Filename"),
            flexGrow: 1,
            width: 140,
            cellRenderer: ({ rowData }) => <FilenameCell file={rowData} />,
          },
          {
            dataKey: "type",
            label: t("Type"),
            width: 70,
            cellRenderer: this.renderFileType,
          },
          {
            dataKey: "size",
            label: t("Size"),
            width: 80,
            cellRenderer: ({ cellData }) => (!isNaN(cellData) ? FormatData.formatBytes(cellData, 1) : null),
          },
          {
            dataKey: "id",
            label: t("Actions"),
            disableSort: true,
            width: 80,
            cellRenderer: ({ rowData }) => (
              // actually rowData is "EmailUploadFile" but it has optional "viewers" and "fileActions" props that are
              // sufficient for DocumentsTableRowActions component to render file actions
              <EmailAttachmentsTableItemActions file={rowData} handleAction={this.onSingleActionSelect} />
            ),
          },
        ];

        return (
          <>
            <Row justify="space-between">
              <EmailAttachmentsTableActions
                resetSelection={this.resetSelection}
                files={this.state.selectedFiles}
                onDownload={this.downloadSelectedFiles}
                disabledDownload={this.selectedFilesCanNotBeDownloaded}
                onRemoveSelectedFiles={this.removeSelectedFiles}
              />
              <div className="flex-center">
                <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                  <Button type="primary">
                    {t("Add files")} <DownOutlined />
                  </Button>
                </Dropdown>
              </div>
            </Row>
            <ScrollableTable<EmailUploadFile>
              rowKey="uid"
              data={data}
              columns={columns}
              onSelectRows={this.handleSelectRows}
              selectedRowKeys={this.state.selectedFileKeys}
              resizeableHeaders={"EMAIL_ATTACHMENTS"}
              ignoreResizeableHeaders={["id", "actions"]}
            />
          </>
        );
      }
    }
  )
);
